import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faAngleDown,
  faAngleUp,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { styled } from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Accordion } from '@sorare/core/src/atoms/layout/Accordion';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Container, FullWidth } from '@sorare/core/src/atoms/layout/Container';
import {
  ContentWithStickyHeader,
  resetStack,
} from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BodyL,
  BodyM,
  HeadlineS,
  HeadlineXL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { Score } from '@sorare/core/src/components/collections/Score';
import {
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
  MLB_PLAY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { groupBy, sortBy } from '@sorare/core/src/lib/arrays';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import {
  breakpoints,
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { CollectionEventPrizePool } from 'components/collections/CollectionEvent/CollectionEventPrizePool';
import {
  ALL_STAR_THRESHOLDS,
  ALL_STAR_THRESHOLDS as COLLECTION_THRESHOLDS_FOR_EVENT,
  CollectionEventProgress,
} from 'components/collections/CollectionEvent/CollectionEventProgress';
import { RarityEntries } from 'components/collections/CollectionEvent/RarityEntries';
import { TeamLogoWithSeasonOverlay } from 'components/collections/CollectionEvent/TeamLogoWithSeasonOverlay';
import { TotalEntries } from 'components/collections/CollectionEvent/TotalEntries';

import type {
  BaseballPlayPlayoff2023CollectionEventQuery,
  BaseballPlayPlayoff2023CollectionEventQueryVariables,
} from './__generated__/page.graphql';
import { prizePool } from './prizePool';

const query = gql`
  query BaseballPlayPlayoff2023CollectionEventQuery(
    $userSlug: String!
    $after: String
  ) {
    user(slug: $userSlug) {
      slug
      cardCollections(
        startedOnly: false
        after: $after
        first: 50
        rarities: [limited, rare, super_rare, unique]
        sport: BASEBALL
      ) {
        nodes {
          slug
          ...CollectionEventProgress_cardCollection
          userCardCollection(forUserSlug: $userSlug) {
            id
            score
            fulfilledSlotsCount
            user {
              slug
            }
          }
          season {
            id
            name
            startYear
          }
          rarity
          team: anyTeam {
            slug
            ...TeamLogoWithSeasonOverlay_team
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${CollectionEventProgress.fragments.cardCollection}
  ${TeamLogoWithSeasonOverlay.fragments.team}
` as TypedDocumentNode<
  BaseballPlayPlayoff2023CollectionEventQuery,
  BaseballPlayPlayoff2023CollectionEventQueryVariables
>;

const PageWrapper = styled.div`
  min-height: var(--100vh);
  margin-bottom: var(--quadruple-unit);
`;

const HeaderWrapper = styled(Horizontal)`
  padding: var(--double-unit) 0;
`;

const Root = styled.div`
  display: grid;
  align-items: flex-start;
  margin-top: var(--quadruple-unit);

  grid-template-areas:
    'summary'
    'main'
    'prizePool';

  gap: var(--quadruple-unit);

  @media ${desktopAndAbove} {
    grid-template-areas:
      'summary main'
      'prizePool main';
    grid-template-columns: 375px 1fr;
    grid-template-rows: auto 1fr;
    gap: var(--quadruple-unit);
  }
`;

const GridAreaMain = styled(Vertical).attrs({ gap: 2 })`
  grid-area: main;

  box-shadow: 0px 0px 128px 64px rgba(163, 84, 81, 0.16);
  --current-background-color: var(--c-nd-100);
  background: var(--c-nd-50);
  border-radius: var(--double-unit);
  overflow: hidden;

  ${resetStack}
`;
const GridAreaSummary = styled(Vertical).attrs({ gap: 2 })`
  grid-area: summary;
`;
const GridAreaPrizePool = styled(Vertical).attrs({ gap: 2 })`
  grid-area: prizePool;
`;

const CollectionGridHeader = styled(Vertical).attrs({ center: true })`
  grid-column: span 2;
  text-align: center;
  font: var(--t-label-l);
  margin-bottom: 0;
`;

const ContentContainer = styled(Vertical).attrs({ gap: 4 })`
  align-self: center;
  flex: 1;
  margin: var(--triple-unit);
  max-width: ${breakpoints.desktop}px;
`;

const NotStartedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--quadruple-unit), 1fr));
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(
      auto-fill,
      minmax(calc(10 * var(--unit)), 1fr)
    );
  }
`;

const FlexList = styled(Horizontal).attrs({ gap: 2 })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
`;

type THRESHOLD_RARITY = keyof typeof COLLECTION_THRESHOLDS_FOR_EVENT;
type CollectionType = NonNullable<
  BaseballPlayPlayoff2023CollectionEventQuery['user']['cardCollections']
>['nodes'][number];

type CollectionDictionary = {
  eligible: CollectionType[];
  started: CollectionType[];
  notStarted: CollectionType[];
  total: number;
};

const EVENT_END_DATE = new Date('2024-06-24T16:00:00.000Z');

const sortByScore = (a: CollectionType, b: CollectionType) =>
  (b.userCardCollection?.score ?? 0) - (a.userCardCollection?.score ?? 0);
const groupByElibility = (items: CollectionType[]) =>
  items.reduce(
    (acc, c) => {
      const collectionRarity = c.rarity as THRESHOLD_RARITY;
      const rarityThreshold = COLLECTION_THRESHOLDS_FOR_EVENT[collectionRarity];
      const score = c.userCardCollection?.score ?? 0;
      const fulfilledSlots = c.userCardCollection?.fulfilledSlotsCount ?? 0;
      if (score >= rarityThreshold) {
        acc.eligible.push(c);
      } else if (fulfilledSlots > 0) {
        acc.started.push(c);
      } else {
        acc.notStarted.push(c);
      }
      acc.total += 1;
      return acc;
    },
    {
      eligible: [],
      started: [],
      notStarted: [],
      total: 0,
    } as CollectionDictionary
  );

const ButtonWrapper = styled(Horizontal).attrs({ gap: 0 })`
  padding: 0 var(--unit);
`;

const InlineFlex = styled(Horizontal)`
  display: inline-flex;
  flex-wrap: wrap;
  row-gap: 0;
`;
const MainWrapper = styled.div``;

const MainBody = styled.div``;

const ToggleButton = styled(ButtonBase)`
  flex: 1;
  color: var(--c-blue-300);
  border-radius: var(--unit);
  white-space: nowrap;
  &:hover,
  &:focus {
    color: var(--c-blue-300);
  }
`;

const ToggleButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--half-unit);
  font: var(--t-body-s);
  color: var(--c-blue-300);
`;

const Padded = styled(Vertical).attrs({ gap: 2 })`
  &.isPrimarySection {
    padding: var(--triple-unit) 0;
    border-top: 1px solid var(--c-nd-100);
  }
`;

const AccordionSection = styled(Vertical).attrs({ gap: 2 })`
  border-top: 1px solid var(--c-nd-100);
`;

const StyledTabBar = styled(TabBar)`
  margin: 0 calc(-1 * var(--double-unit));

  &.flat {
    --bar-bg-color: var(--c-nd-100) !important;
    --animated-border-bottom: 2px solid var(--c-active-tab-border);
    --button-border-radius: 24px 24px 0 0;
  }
`;

const StyledTab = styled(Tab)`
  flex: 1 !important;
`;
const Title = styled.div`
  > *:first-child {
    font: var(--t-label-s);
  }
  > *:only-child,
  > *:nth-child(2) {
    font: var(--t-headline-l);
  }
`;

const TabBarScarcityLabel = styled.h3`
  font: var(--t-body-s);
`;

const FREE_ENTRIES_PER_USER = 1;
const AllStarCollectionEventPage = () => {
  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const [searchParams] = useSearchParams();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { formatMessage } = useIntl();
  const { track } = useEventsContext();
  const [selectedRarity, setSelectedRarity] = useState<THRESHOLD_RARITY>(
    Rarity.limited
  );
  const [viewMore, setViewMore] = useState(false);
  const userSlug = searchParams.get('user') || currentUser?.slug || '';

  const [loadedEndCursor, setLoadedEndCursor] = useState<string | null>(null);

  const { data, loading, loadMore } = usePaginatedQuery(query, {
    variables: {
      userSlug,
    },
    skip: !userSlug,
    connection: 'CardCollectionConnection',
    fetchPolicy: 'network-only',
  });

  const collections = (data?.user?.cardCollections?.nodes || []).filter(
    c => c.rarity && c.rarity !== Rarity.common
  );

  const pageInfo = data?.user?.cardCollections?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage;
  const endCursor = pageInfo?.endCursor ?? null;

  // fetch again until there we have all collections
  useEffect(() => {
    if (hasNextPage && endCursor !== loadedEndCursor) {
      loadMore(false, { after: endCursor });
      setLoadedEndCursor(endCursor);
    }
  }, [endCursor, hasNextPage, loadMore, loadedEndCursor]);

  const collectionsPerRarity = useMemo(() => {
    const sorted = Object.entries(
      groupBy(collections, c => c.rarity || 'no_rarity')
    ).map(([cRarity, rarityCollections]) => [
      cRarity,
      groupByElibility(rarityCollections.sort(sortByScore)),
    ]);
    return Object.fromEntries(sorted);
  }, [collections]);

  const rarityCollections = useMemo(() => {
    const sorted = collections
      .filter(c => c.rarity === (selectedRarity as any))
      .sort(sortByScore);
    return groupByElibility(sorted);
  }, [collections, selectedRarity]);

  const passThreshold = collections.reduce(
    (acc, collection) => {
      const collectionRarity = collection.rarity as THRESHOLD_RARITY;
      if (!collection.userCardCollection) {
        return acc;
      }
      const passed =
        collection.userCardCollection.score >=
        COLLECTION_THRESHOLDS_FOR_EVENT[collectionRarity];
      const inSeason = collection.season?.startYear === 2024;
      const value = passed ? 1 : 0;
      return {
        inSeason: acc.inSeason + (inSeason ? value : 0),
        classic: acc.classic + (!inSeason ? value : 0),
      };
    },
    { inSeason: 0, classic: 0 }
  );

  const passThresholdPerRarity = collections.reduce(
    (acc, collection) => {
      const collectionRarity = collection.rarity as THRESHOLD_RARITY;
      if (!collection.userCardCollection) {
        return { ...acc };
      }
      const passed =
        collection.userCardCollection.score >=
        COLLECTION_THRESHOLDS_FOR_EVENT[collectionRarity];
      return {
        ...acc,
        [collectionRarity]: acc[collectionRarity] + (passed ? 1 : 0),
      };
    },
    {
      [Rarity.common]: 0,
      [Rarity.limited]: 0,
      [Rarity.rare]: 0,
      [Rarity.super_rare]: 0,
      [Rarity.unique]: 0,
    }
  );

  const passedThresholdSelectedRarity =
    passThresholdPerRarity[selectedRarity] > 0;
  const startedItemsToDisplay = isTabletAndAbove ? 10 : 5;
  const showEligibleSection = rarityCollections.eligible.length > 0;
  const firstFive = rarityCollections.started.slice(0, startedItemsToDisplay);
  const remainingOthers = rarityCollections.started.slice(
    startedItemsToDisplay,
    rarityCollections.started.length
  );
  const showEmptySection = rarityCollections.notStarted.length > 0;
  const showStartedSection = rarityCollections.started.length > 0;
  const showMore = rarityCollections.started.length > startedItemsToDisplay;

  const notStartedSortedByTeamName = sortBy(
    c => c.team?.slug || '',
    rarityCollections.notStarted
  );

  const onTabClick = useCallback(
    (rarity: THRESHOLD_RARITY) => {
      setSelectedRarity(rarity);

      const limitedEntries = collectionsPerRarity[Rarity.limited];
      const rareEntries = collectionsPerRarity[Rarity.rare];
      const superRareEntries = collectionsPerRarity[Rarity.super_rare];
      const uniqueEntries = collectionsPerRarity[Rarity.unique];
      track('Change Collection Event PageTab', {
        sport: Sport.BASEBALL,
        fromRarity: selectedRarity,
        toRarity: rarity,
        summary: {
          limited_eligible: limitedEntries.eligible.length,
          limited_started: limitedEntries.started.length,
          rare_eligible: rareEntries.eligible.length,
          rare_started: rareEntries.started.length,
          super_rare_eligible: superRareEntries.eligible.length,
          super_rare_started: superRareEntries.started.length,
          unique_eligible: uniqueEntries.eligible.length,
          unique_started: uniqueEntries.started.length,
        },
      });
    },
    [collectionsPerRarity, selectedRarity, track]
  );

  const isLoading = Boolean(!data || loading || hasNextPage);

  return (
    <PageWrapper>
      <ContentWithStickyHeader
        header={
          <FullWidth>
            <Container>
              <HeaderWrapper>
                <IconButton
                  size="medium"
                  color="transparent"
                  icon={faTimes}
                  title={formatMessage(glossary.close)}
                  to={MLB_PLAY}
                />

                <Title>
                  <h2>
                    <Ellipsis>
                      {formatMessage({
                        id: 'Baseball.CollectionEvent.pageTitle',
                        defaultMessage: 'All-Star Collection Event',
                      })}
                    </Ellipsis>
                  </h2>
                </Title>
              </HeaderWrapper>
            </Container>
          </FullWidth>
        }
      >
        <Container>
          <Root>
            <GridAreaSummary center gap={isTabletAndAbove ? 4 : 2}>
              <Vertical center>
                <TotalEntries
                  collectionEntries={passThreshold}
                  freeEntries={FREE_ENTRIES_PER_USER}
                  loading={isLoading}
                />
                <LabelS as="div" color="var(--c-nd-600)">
                  <TimeLeft time={EVENT_END_DATE}>
                    {({ timeLeft, isEnded }) =>
                      isEnded ? (
                        <FormattedMessage
                          id="Baseball.CollectionEvent.eventEnded"
                          defaultMessage="This event ended"
                        />
                      ) : (
                        <FormattedMessage
                          id="Baseball.CollectionEvent.eventEndsIn"
                          defaultMessage="This event will end in {timeLeft}"
                          values={{
                            timeLeft: (
                              <LabelS as="span" bold color="var(--c-white)">
                                {timeLeft}
                              </LabelS>
                            ),
                          }}
                        />
                      )
                    }
                  </TimeLeft>
                </LabelS>
              </Vertical>
            </GridAreaSummary>

            <GridAreaPrizePool>
              <Vertical>
                <Horizontal gap={0}>
                  <HeadlineS as="span" color="var(--c-white)">
                    <FormattedMessage {...fantasy.prizes} />
                  </HeadlineS>{' '}
                  <BodyL as="span" color="var(--c-nd-600)">
                    *
                  </BodyL>
                </Horizontal>
                <Box>
                  <Vertical gap={2}>
                    <CollectionEventPrizePool prizePool={prizePool} />
                  </Vertical>
                </Box>
                <LabelS as="span" color="var(--c-nd-400)">
                  *{' '}
                  <FormattedMessage
                    id="Baseball.CollectionEvent.TermsAndConditionsLink"
                    defaultMessage="Terms & Conditions apply. <link>Read more</link>."
                    values={{
                      link: (children: ReactNode[]) => (
                        <Link to="/blog/official-rules">
                          <LabelS as="span" bold color="var(--c-nd-400)">
                            {children}
                          </LabelS>
                        </Link>
                      ),
                    }}
                  />
                </LabelS>
              </Vertical>
            </GridAreaPrizePool>
            <GridAreaMain>
              <MainWrapper>
                <FullWidthContainer.NoPadding>
                  <ContentWithStickyHeader
                    header={
                      <Container
                        style={
                          {
                            '--c-active-tab-bg': getRarityColor(
                              selectedRarity,
                              0.1
                            ),
                            '--c-active-tab-border':
                              getRarityColor(selectedRarity),
                          } as CSSProperties
                        }
                      >
                        <StyledTabBar
                          fullWidth
                          variant="flat"
                          value={selectedRarity}
                        >
                          {(
                            [
                              Rarity.limited,
                              Rarity.rare,
                              Rarity.super_rare,
                              Rarity.unique,
                            ] as THRESHOLD_RARITY[]
                          ).map(rarity => {
                            return (
                              <StyledTab
                                key={rarity}
                                value={rarity}
                                onClick={() => onTabClick(rarity)}
                              >
                                <CollectionGridHeader>
                                  <Vertical center gap={0}>
                                    <Vertical center gap={1}>
                                      <Skeleton loading={isLoading}>
                                        <RarityEntries
                                          nb={passThresholdPerRarity[rarity]}
                                          max={
                                            collectionsPerRarity[rarity]
                                              ?.total || 0
                                          }
                                        />
                                      </Skeleton>
                                      <TabBarScarcityLabel>
                                        <ScarcityLabel scarcity={rarity} />
                                      </TabBarScarcityLabel>
                                    </Vertical>
                                    <Horizontal center gap={1}>
                                      <LabelS bold>
                                        <Horizontal>
                                          {!!isTabletAndAbove && (
                                            <FormattedMessage
                                              id="Baseball.CollectionEvent.RequiresScore"
                                              defaultMessage="Requires {nb}"
                                              values={{
                                                nb: (
                                                  <Score
                                                    score={
                                                      COLLECTION_THRESHOLDS_FOR_EVENT[
                                                        Rarity[rarity]
                                                      ]
                                                    }
                                                  />
                                                ),
                                              }}
                                            />
                                          )}
                                        </Horizontal>
                                      </LabelS>
                                    </Horizontal>
                                  </Vertical>
                                </CollectionGridHeader>
                              </StyledTab>
                            );
                          })}
                        </StyledTabBar>
                      </Container>
                    }
                  >
                    <MainBody key={selectedRarity}>
                      {!isLoading && (
                        <ContentContainer>
                          {!!showEligibleSection && (
                            <Vertical gap={3}>
                              <Vertical gap={0.5}>
                                <BodyL as="div" bold>
                                  <InlineFlex>
                                    <FormattedMessage
                                      id="Baseball.CollectionEvent.EligibleSection.Title"
                                      defaultMessage="Congrats, you hit the {score} threshold !"
                                      values={{
                                        score: (
                                          <Score
                                            score={
                                              COLLECTION_THRESHOLDS_FOR_EVENT[
                                                selectedRarity
                                              ]
                                            }
                                          />
                                        ),
                                      }}
                                    />
                                  </InlineFlex>
                                </BodyL>
                                <LabelS as="div" color="var(--c-nd-600)">
                                  <FormattedMessage
                                    id="Baseball.CollectionEvent.EligibleSection.Subtitle"
                                    defaultMessage="Each Collection gets you 1 entry to this event"
                                  />
                                </LabelS>
                              </Vertical>
                              <FlexList>
                                {rarityCollections.eligible.map(collection => {
                                  return (
                                    <CollectionEventProgress
                                      key={collection.slug}
                                      userSlug={userSlug}
                                      collection={collection}
                                      thresholds={ALL_STAR_THRESHOLDS}
                                    />
                                  );
                                })}
                              </FlexList>
                            </Vertical>
                          )}
                          {!showEligibleSection && !showStartedSection && (
                            <Vertical gap={3}>
                              <Vertical gap={0.5}>
                                <BodyL as="div" bold>
                                  <Horizontal>
                                    <FormattedMessage
                                      id="Baseball.CollectionEvent.EligibleSection.Title.empty"
                                      defaultMessage="Start a collection for chance to win prizes"
                                    />
                                  </Horizontal>
                                </BodyL>
                                <LabelS as="div" color="var(--c-nd-600)">
                                  <FormattedMessage
                                    id="Baseball.CollectionEvent.EligibleSection.Subtitle"
                                    defaultMessage="Each Collection gets you 1 entry to this event"
                                  />
                                </LabelS>
                              </Vertical>
                            </Vertical>
                          )}
                          {!!showStartedSection && (
                            <Padded
                              gap={3}
                              className={classNames({
                                isPrimarySection: showEligibleSection,
                              })}
                            >
                              <Vertical gap={0.5}>
                                {passedThresholdSelectedRarity ? (
                                  <BodyM as="div" bold>
                                    <FormattedMessage
                                      id="Baseball.CollectionEvent.StartedSection.Title.another"
                                      defaultMessage="Add to these collections to get another entry:"
                                    />
                                  </BodyM>
                                ) : (
                                  <BodyL as="div" bold>
                                    <FormattedMessage
                                      id="Baseball.CollectionEvent.StartedSection.Title.first"
                                      defaultMessage="Add to these collections to get a chance to win prizes"
                                    />
                                  </BodyL>
                                )}
                                <LabelS as="div" color="var(--c-nd-600)">
                                  {passedThresholdSelectedRarity ? (
                                    <FormattedMessage
                                      id="Baseball.CollectionEvent.StartedSection.Subtitle"
                                      defaultMessage="Every entry gives you a better chance to win prizes"
                                    />
                                  ) : (
                                    <FormattedMessage
                                      id="Baseball.CollectionEvent.EligibleSection.Subtitle"
                                      defaultMessage="Each Collection gets you 1 entry to this event"
                                    />
                                  )}
                                </LabelS>
                              </Vertical>
                              <Vertical gap={0}>
                                <FlexList>
                                  {firstFive.map(collection => {
                                    return (
                                      <CollectionEventProgress
                                        key={collection.slug}
                                        userSlug={userSlug}
                                        collection={collection}
                                        thresholds={ALL_STAR_THRESHOLDS}
                                      />
                                    );
                                  })}
                                </FlexList>

                                {!!showMore && (
                                  <>
                                    <Collapse
                                      key={selectedRarity}
                                      open={viewMore}
                                      gap={0}
                                    >
                                      <Vertical gap={2}>
                                        <Vertical center>
                                          <HeadlineXL
                                            as="div"
                                            color="var(--c-nd-200)"
                                          >
                                            • • •
                                          </HeadlineXL>
                                        </Vertical>
                                        <FlexList>
                                          {remainingOthers.map(collection => {
                                            return (
                                              <CollectionEventProgress
                                                key={collection.slug}
                                                userSlug={userSlug}
                                                collection={collection}
                                                thresholds={ALL_STAR_THRESHOLDS}
                                              />
                                            );
                                          })}
                                        </FlexList>
                                      </Vertical>
                                    </Collapse>
                                    <ButtonWrapper>
                                      <ToggleButton
                                        onClick={() => setViewMore(v => !v)}
                                      >
                                        <ToggleButtonContent>
                                          <FormattedMessage
                                            {...(viewMore
                                              ? glossary.showLess
                                              : glossary.showMore)}
                                          />
                                          <FontAwesomeIcon
                                            icon={
                                              viewMore ? faAngleUp : faAngleDown
                                            }
                                            size="sm"
                                          />
                                        </ToggleButtonContent>
                                      </ToggleButton>
                                    </ButtonWrapper>
                                  </>
                                )}
                              </Vertical>
                            </Padded>
                          )}
                          {!!showEmptySection && (
                            <AccordionSection gap={2}>
                              <Accordion
                                key={selectedRarity}
                                startsOpen={!showStartedSection}
                                noTopPadding
                                noHorizontalPadding
                                title={
                                  <Vertical center>
                                    <LabelS
                                      as="div"
                                      bold
                                      color="var(--c-nd-600)"
                                    >
                                      <FormattedMessage
                                        id="Baseball.CollectionEvent.NotStartedSection.Title"
                                        defaultMessage="Not started ({nb})"
                                        values={{
                                          nb: rarityCollections.notStarted
                                            .length,
                                        }}
                                      />
                                    </LabelS>
                                  </Vertical>
                                }
                              >
                                <NotStartedGrid>
                                  {notStartedSortedByTeamName.map(
                                    collection => {
                                      if (
                                        !collection.team ||
                                        !collection.season
                                      ) {
                                        return null;
                                      }
                                      return (
                                        <TeamLogoWithSeasonOverlay
                                          key={collection.slug}
                                          team={collection.team}
                                          onClick={() =>
                                            track(
                                              'Clicking Collection Event CollectionItem',
                                              {
                                                sport: Sport.BASEBALL,
                                                filledSlots: 0,
                                                rarity: selectedRarity,
                                                score: 0,
                                                passThreshold: false,
                                                season:
                                                  collection.season!.startYear,
                                              }
                                            )
                                          }
                                          season={collection.season.startYear}
                                          to={generateSportContextPath(
                                            ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
                                            {
                                              params: {
                                                slug: userSlug,
                                                collection: collection.slug,
                                              },
                                            }
                                          )}
                                        />
                                      );
                                    }
                                  )}
                                </NotStartedGrid>
                              </Accordion>
                            </AccordionSection>
                          )}
                        </ContentContainer>
                      )}
                    </MainBody>
                  </ContentWithStickyHeader>
                </FullWidthContainer.NoPadding>
              </MainWrapper>
            </GridAreaMain>
          </Root>
        </Container>
      </ContentWithStickyHeader>
    </PageWrapper>
  );
};

export default AllStarCollectionEventPage;
